import { useCallback } from 'react';

import { useTheme } from '@mui/material';
import { XAxisOptions } from 'highcharts';

import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useSeasonalTooltipFormatter from '@/components/Chart/hooks/tooltip/useSeasonalTooltipFormatter';
import { ExtendedOptions, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { getDayMonthOfYear } from '@/components/Chart/utils/getDayMonthOfYear';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { regularChartPalette } from '@/constants/constants';

import useForwardCurveOptions from './useForwardCurveOptions';

const useSeasonalOptions = (height?: number) => {
  const { createOptions: createFvCurveOptions } = useForwardCurveOptions(height);
  const { createTooltipFormatter } = useSeasonalTooltipFormatter();
  const { formatDateString } = useDateText();
  const theme = useTheme();
  const { tooltip } = getRegularTooltip(createTooltipFormatter, theme);
  const createOptions = useCallback((chartSeries: ExtendedSeriesOptionsType[]): ExtendedOptions => {
    chartSeries = chartSeries.map(series => {
      const { combinedIndex, field } = series.custom;

      if (series.type === 'arearange') {
        series.color = '#cdcdb5';
        series.fillOpacity = 0.2;
      } else {
        if (field === 'average') {
          series.color = '#F8F8F8';
          return series;
        }
        if (!Number.isNaN(Number(field))) {
          const yearToYearIndex = new Date().getFullYear() - Number(field);
          series.color = regularChartPalette[yearToYearIndex];
          return series;
        }
        series.color = regularChartPalette[combinedIndex % regularChartPalette.length];
      }
      return series;
    });

    let options = {
      ...createFvCurveOptions(chartSeries)
    };
    const xAxisOptions = options.xAxis as XAxisOptions | undefined;
    if (xAxisOptions) {
      xAxisOptions.tickInterval = 31;
      xAxisOptions.min = -5;
      if (xAxisOptions.labels)
        xAxisOptions.labels.formatter = function (): string {
          const value = Number(this.value);
          return formatDateString(getDayMonthOfYear(value + 1).toJSON(), DateTextVariants.MONTH);
        };
    }
    options = { ...options, xAxis: xAxisOptions };
    options = { ...options, tooltip };
    return options;
  }, []);

  return { createOptions };
};

export default useSeasonalOptions;
